/**
 * TEKRARLAYAN ETKINLIK — F-CAL-06b
 *
 * Bu dosya SAF: veritabani, NestJS ve HTTP bilmez. Girdisi bir RRULE
 * dizgisi ve bir zaman penceresi, ciktisi o penceredeki OLUSLAR.
 *
 * ================================================================
 * NEDEN VAR
 * ================================================================
 * 29.08.2026'da olculdu: `CalendarEvent.recurrenceRule` kaydediliyor
 * ama HICBIR YERDE okunmuyordu. Kayit yalnizca ILK olusuyla duruyor;
 * gelecek haftaya bakan musaitlik sorgusu onu hic gormuyordu.
 *
 * Yani sistem ogrenciyi okul saatinde BOS gosteriyor, koc oraya
 * gorusme koyabiliyordu. Alan vardi, sozu tutulmuyordu.
 *
 * ================================================================
 * OLUSLAR VERITABANINA YAZILMIYOR
 * ================================================================
 * Tekrar tek satir kaliyor ve sorgu aninda aciliyor. Yazilsaydi bir
 * donem icin binlerce satir uretilir ve kural degistiginde hepsinin
 * temizlenmesi gerekirdi — temizlenmeyen bir tanesi de kalici hayalet
 * mesguliyet olurdu.
 *
 * ================================================================
 * DESTEKLENMEYEN KURAL SESSIZCE SAKLANMAZ
 * ================================================================
 * `cozumle` taniyamadigi kurala `null` degil HATA donduruyor. Sessiz
 * kabul, tam da bu dosyanin dogdugu hatanin kendisi olurdu.
 *
 * ================================================================
 * NEDEN CONTRACTS'TA — Codex tur 36 B5 (05.10.2026)
 * ================================================================
 * Dosya `apps/api/src/modules/takvim/` altindaydi. Panelin haftalik plan
 * tablosu (`plan-tanimi.ts`) kurali KENDISI yorumluyordu ve yalnizca
 * BYDAY'e bakiyordu: INTERVAL=2 her hafta, gecmiste biten UNTIL hala
 * gorunuyordu — program ekrani (sunucu hesabi) ile plan ekrani ayni
 * ogrenciye farkli mesguliyet gosteriyordu. Simdi iki taraf AYNI kodu
 * calistiriyor. Saf ve bagimliliksiz; hesap UTC gunleri uzerinden.
 */

export interface Aralik {
  baslangic: Date;
  bitis: Date;
}

export type TekrarHatasi =
  | "BICIM"
  | "FREQ_DESTEKLENMIYOR"
  | "GUN_GECERSIZ"
  | "INTERVAL_GECERSIZ"
  | "UNTIL_GECERSIZ"
  | "COUNT_GECERSIZ";

export const TEKRAR_MESAJI: Record<TekrarHatasi, string> = {
  BICIM: "Tekrar kuralı `FREQ=WEEKLY;...` biçiminde olmalı.",
  FREQ_DESTEKLENMIYOR: "Yalnızca haftalık tekrar destekleniyor (FREQ=WEEKLY).",
  GUN_GECERSIZ: "BYDAY yalnızca MO, TU, WE, TH, FR, SA, SU alabilir.",
  INTERVAL_GECERSIZ: "INTERVAL 1 ile 4 arasında olmalı.",
  UNTIL_GECERSIZ: "UNTIL `YYYYAAGG` biçiminde bir tarih olmalı.",
  COUNT_GECERSIZ: "COUNT 1 ile 200 arasında olmalı.",
};

/** RRULE gun kisaltmalari — `Date.getUTCDay()` sirasi (0 = Pazar). */
const GUNLER = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"] as const;

export interface TekrarKurali {
  /** Haftanin gunleri, `getUTCDay()` degerleri (0–6) */
  gunler: number[];
  /** Kac haftada bir */
  aralikHafta: number;
  /** Bu tarihten SONRA olus uretilmez (dahil) */
  bitisTarihi: Date | null;
  /** En fazla kac olus */
  azamiOlus: number | null;
}

export class TekrarKuralHatasi extends Error {
  constructor(readonly kod: TekrarHatasi) {
    super(TEKRAR_MESAJI[kod]);
    this.name = "TekrarKuralHatasi";
  }
}

/**
 * RRULE alt kumesini cozumler. Taninmayan her sey HATA.
 *
 * `baslangic` gerekiyor cunku `BYDAY` verilmediginde tekrar, ilk
 * olusun gununde yapilir (RFC 5545 davranisi).
 */
export function cozumle(ham: string, baslangic: Date): TekrarKurali {
  const parcalar = new Map<string, string>();
  for (const bolum of ham.trim().split(";")) {
    if (bolum.length === 0) continue;
    const esit = bolum.indexOf("=");
    if (esit <= 0) throw new TekrarKuralHatasi("BICIM");
    parcalar.set(bolum.slice(0, esit).trim().toUpperCase(), bolum.slice(esit + 1).trim());
  }

  const freq = parcalar.get("FREQ");
  if (!freq) throw new TekrarKuralHatasi("BICIM");
  if (freq.toUpperCase() !== "WEEKLY") throw new TekrarKuralHatasi("FREQ_DESTEKLENMIYOR");

  // BYDAY yoksa: baslangicin gunu.
  let gunler = [baslangic.getUTCDay()];
  const byday = parcalar.get("BYDAY");
  if (byday !== undefined) {
    const kodlar = byday
      .split(",")
      .map((g) => g.trim().toUpperCase())
      .filter((g) => g.length > 0);
    if (kodlar.length === 0) throw new TekrarKuralHatasi("GUN_GECERSIZ");
    gunler = kodlar.map((k) => {
      const i = GUNLER.indexOf(k as (typeof GUNLER)[number]);
      if (i < 0) throw new TekrarKuralHatasi("GUN_GECERSIZ");
      return i;
    });
    gunler = [...new Set(gunler)].sort((a, b) => a - b);
  }

  let aralikHafta = 1;
  const interval = parcalar.get("INTERVAL");
  if (interval !== undefined) {
    const n = Number(interval);
    if (!Number.isInteger(n) || n < 1 || n > 4) throw new TekrarKuralHatasi("INTERVAL_GECERSIZ");
    aralikHafta = n;
  }

  let bitisTarihi: Date | null = null;
  const until = parcalar.get("UNTIL");
  if (until !== undefined) {
    // `YYYYAAGG` — saat bileseni kabul edilmiyor; mesguliyet gun
    // bazinda tanimlaniyor ve saatli bir UNTIL yanlis guven verirdi.
    const m = /^(\d{4})(\d{2})(\d{2})$/.exec(until);
    if (!m) throw new TekrarKuralHatasi("UNTIL_GECERSIZ");
    const yil = Number(m[1]);
    const ay = Number(m[2]);
    const gun = Number(m[3]);
    const t = new Date(Date.UTC(yil, ay - 1, gun, 23, 59, 59, 999));
    if (Number.isNaN(t.getTime()) || t.getUTCMonth() !== ay - 1 || t.getUTCDate() !== gun) {
      throw new TekrarKuralHatasi("UNTIL_GECERSIZ");
    }
    bitisTarihi = t;
  }

  let azamiOlus: number | null = null;
  const count = parcalar.get("COUNT");
  if (count !== undefined) {
    const n = Number(count);
    if (!Number.isInteger(n) || n < 1 || n > 200) throw new TekrarKuralHatasi("COUNT_GECERSIZ");
    azamiOlus = n;
  }

  // Taninmayan anahtar SESSIZCE ATLANMIYOR: `EXDATE` ya da `BYMONTH`
  // yazan biri, kuralinin kismen uygulandigini sanirdi.
  for (const anahtar of parcalar.keys()) {
    if (!["FREQ", "BYDAY", "INTERVAL", "UNTIL", "COUNT"].includes(anahtar)) {
      throw new TekrarKuralHatasi("BICIM");
    }
  }

  return { gunler, aralikHafta, bitisTarihi, azamiOlus };
}

/**
 * Kuralı pencerede acar.
 *
 * `ilk` olusun SURESI her tekrarda korunuyor: okul 08:00–15:00 ise her
 * hafta yedi saat. Sure yerine bitis saati kopyalansaydi, gece yarisini
 * asan bir mesguliyet ertesi gune tasinirdi.
 *
 * Pencere disi olus uretilmiyor; cagiran taraf zaten yalnizca o araligi
 * soruyor.
 */
export function oluslar(
  ilk: Aralik,
  kural: TekrarKurali,
  pencere: Aralik,
  azamiGuvenlik = 400,
): Aralik[] {
  const sureMs = ilk.bitis.getTime() - ilk.baslangic.getTime();
  if (sureMs <= 0) return [];

  const sonuc: Aralik[] = [];
  const gun = 24 * 60 * 60 * 1000;

  // Ilk olusun HAFTASININ pazar gunu — hafta sayaci buradan ilerliyor.
  const ilkGunBasi = Date.UTC(
    ilk.baslangic.getUTCFullYear(),
    ilk.baslangic.getUTCMonth(),
    ilk.baslangic.getUTCDate(),
  );
  const haftaBasi = ilkGunBasi - ilk.baslangic.getUTCDay() * gun;

  // Gunun saati ilk olustan aliniyor.
  const gunIcindeMs = ilk.baslangic.getTime() - ilkGunBasi;

  const sinir = kural.bitisTarihi
    ? Math.min(pencere.bitis.getTime(), kural.bitisTarihi.getTime())
    : pencere.bitis.getTime();

  let sayac = 0;
  for (let hafta = 0; hafta < azamiGuvenlik; hafta++) {
    if (hafta % kural.aralikHafta !== 0) continue;
    const haftaninBasi = haftaBasi + hafta * 7 * gun;

    // Bu hafta tamamen pencerenin ilerisindeyse bitir.
    if (haftaninBasi > sinir) break;

    for (const g of kural.gunler) {
      const baslangicMs = haftaninBasi + g * gun + gunIcindeMs;
      // Ilk olustan ONCE olus uretilmiyor: BYDAY, baslangic gununden
      // once bir gun iceriyorsa o hafta atlanir.
      if (baslangicMs < ilk.baslangic.getTime()) continue;
      if (baslangicMs > sinir) continue;
      /*
        COUNT SERININ BASINDAN SAYILIR (05.10.2026, B5 karsilastirmali
        testinde bulundu). Sayac pencere suzgecinden SONRA artiyordu:
        `COUNT=3` bir kural, ilk uc haftadan aylar sonraki bir pencerede
        yine uc olus uretiyordu — bitmis bir okul kaydi her hafta dolu
        gorunurdu. Pencere disindaki olus da serinin bir olusu.
      */
      sayac++;
      const bitisMs = baslangicMs + sureMs;
      // Pencereyle KESISIYOR mu — tamamen icinde olmasi gerekmiyor.
      if (bitisMs > pencere.baslangic.getTime()) {
        sonuc.push({ baslangic: new Date(baslangicMs), bitis: new Date(bitisMs) });
      }
      if (kural.azamiOlus !== null && sayac >= kural.azamiOlus) {
        return sonuc.sort((a, b) => a.baslangic.getTime() - b.baslangic.getTime());
      }
    }
  }

  return sonuc.sort((a, b) => a.baslangic.getTime() - b.baslangic.getTime());
}

/**
 * Bir etkinligi pencerede dolu araliklara cevirir.
 *
 * Kural yoksa etkinligin kendisi tek olus. Kural BOZUKSA da tek olus
 * doner — cagiran taraf musaitlik hesabi yapiyor ve orada patlamak,
 * eski bir kaydin bir gun butun takvimi kilitlemesi demek olurdu.
 * Bozuk kural YAZILIRKEN reddediliyor; bu yol yalnizca gecmis veriye
 * karsi bir emniyet.
 */
export function etkinliginOluslari(
  etkinlik: Aralik & { tekrarKurali: string | null },
  pencere: Aralik,
): Aralik[] {
  if (!etkinlik.tekrarKurali) {
    return [{ baslangic: etkinlik.baslangic, bitis: etkinlik.bitis }];
  }
  let kural: TekrarKurali;
  try {
    kural = cozumle(etkinlik.tekrarKurali, etkinlik.baslangic);
  } catch {
    return [{ baslangic: etkinlik.baslangic, bitis: etkinlik.bitis }];
  }
  return oluslar({ baslangic: etkinlik.baslangic, bitis: etkinlik.bitis }, kural, pencere);
}
